import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { VirtualizedList } from 'react-native';
import styled from 'styled-components/native';

import { ICountryStoneinnoDetail } from '@webtrader/core/api/account/country-stoneinno';
import { ECustomerType } from '@webtrader/core/api/account/signUp';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useTranslation } from '@webtrader/core/i18n';
import { countryService, useCountry } from '@webtrader/core/services/country';
import { useTheme } from '@webtrader/core/services/theme/v2';

import ActionSheetSelector from '@/components/ActionSheet/Selectors';
import ValidationError from '@/components/FormValidationError';
import FeatherIcons from '@/components/Icons/Feather';
import Modal from '@/components/Modal';
import TextInput from '@/components/TextInput';
import TouchableOpacity from '@/components/Touch/Opacity';

import CountrySelector from './Selector';

interface IProps {
  value?: number;
  disabled?: boolean;
  onSelect: (countryId: number) => void;
  customerType?: ECustomerType;
  showValidation: boolean;
}

const SignUpFormCountry: FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { value, disabled = false, onSelect, customerType = ECustomerType.INDIVIDUAL, showValidation } = props;

  const [keyword, setKeyword] = useState('');
  const [showOptions, setShowOptions] = useState(false);

  useEffect(() => {
    countryService.init(true);
  }, []);

  const { theme } = useTheme();
  const { countryStoneinnoDetails } = useCountry();

  const onOpen = () => setShowOptions(true);
  const onClose = () => setShowOptions(false);

  const onSelectCountry = useCallback(
    (countryId: number) => {
      onClose();
      onSelect(countryId);
    },
    [onSelect]
  );

  const filteredCountries = useMemo(() => {
    return countryStoneinnoDetails.filter(({ country }) =>
      country.replace(' ', '').toLowerCase().includes(keyword.replace(' ', '').toLowerCase())
    );
  }, [keyword, countryStoneinnoDetails]);

  const label = useMemo(() => {
    if (value === undefined) {
      return '';
    }
    return filteredCountries.find((c) => c.id === value)?.country || '';
  }, [filteredCountries, value]);

  const title = customerType === ECustomerType.INDIVIDUAL ? t('Country of Residence') : t('Country of Registration');
  const validationMessage = title + ' ' + t('is required');

  return (
    <>
      <ActionSheet onPress={onOpen} disabled={disabled}>
        <ActionSheetSelector
          title={title}
          label={label}
          withBorder={true}
          testID={EDataTestId.SIGN_UP_COUNTRY_SELECTOR}
          placeholder={t('Select Country')}
        />
      </ActionSheet>
      <Modal title={title} visible={showOptions} onClose={onClose}>
        <ModalContentSearch>
          <TextInput
            value={keyword}
            prefix={<FeatherIcons name="search" size={18} color={theme.colors.text[2]} />}
            placeholder="Search"
            onChangeText={setKeyword}
            testID={EDataTestId.SIGN_UP_COUNTRY_SEARCH_INPUT}
          />
        </ModalContentSearch>
        <VirtualizedList<ICountryStoneinnoDetail>
          data={filteredCountries}
          getItem={(data, i) => data[i]}
          getItemCount={() => filteredCountries.length}
          keyExtractor={(item) => item.country}
          initialNumToRender={30}
          renderItem={({ item }) => {
            return <CountrySelector country={item} selected={value === item.id} onSelect={onSelectCountry} />;
          }}
        />
      </Modal>
      <ValidationError showValidation={showValidation} message={validationMessage} />
    </>
  );
};

export default memo(SignUpFormCountry);

const ModalContentSearch = styled.View`
  padding: ${dynamicSize.convertSize(10)}px;
`;

const ActionSheet = styled(TouchableOpacity)``;
